import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import I18n from 'i18n-js';
import { DatePicker } from 'grab-ui';
import Select from 'components/v2/select/select';
import shopifyLogo from 'public/images/shopify-logo.png';

import { isPaxSelector } from 'selectors/app-selector';
import {
  historyBookingFilterSelector,
  historyDeliveriesV2Selector,
} from 'selectors/new-history-selectors';
import {
  GFB_ORDER_HISTORY_FETCH_DAYS,
  GFB_ORDER_HISTORY_INIT_FETCH_DAYS,
  OLDEST_HISTORY_FETCH_DAYS,
  PAX_ORDER_HISTORY_FETCH_DAYS,
  PAX_ORDER_HISTORY_INIT_FETCH_DAYS,
} from 'constants/history-constants';
import {
  GE_WEB_ORDER_STATE,
  NEW_COMPLETED_ORDER_STATE_FILTER,
  ORDER_TYPES,
} from 'constants/order-constants';
import ShopifyTrackEventContext from 'views/v2/shopify-home/track-event';
import {
  SCRIBE_STATE_NAMES,
  SCRIBE_SHOPIFY_EVENTS,
} from 'applications/scribe/instrumentations/track';
import NewHistoryDuck from 'ducks/new-history-duck';
import ScribeDuck from 'ducks/scribe-duck';
import TrackGADuck from 'ducks/track-ga-duck';
import commonTable from './common-table.less';

const HistoryBookingFilter = () => {
  const dispatch = useDispatch();
  const shopifyTrackCtx = useContext(ShopifyTrackEventContext);

  const isPax = useSelector(isPaxSelector);
  const { orderState, orderType, startDate, endDate } = useSelector(
    historyBookingFilterSelector,
  );
  const { loading } = useSelector(historyDeliveriesV2Selector);
  const [tempStartDate, setTempStartDate] = useState();
  const dayRange = useMemo(
    () => (isPax ? PAX_ORDER_HISTORY_FETCH_DAYS : GFB_ORDER_HISTORY_FETCH_DAYS),
    [isPax],
  );
  const initDayRange = useMemo(
    () =>
      isPax
        ? PAX_ORDER_HISTORY_INIT_FETCH_DAYS
        : GFB_ORDER_HISTORY_INIT_FETCH_DAYS,
    [isPax],
  );

  // init filter
  useEffect(
    () => {
      dispatch(
        NewHistoryDuck.creators.updateHistoryBookingFilter({
          orderType: ORDER_TYPES.ALL,
          orderState: GE_WEB_ORDER_STATE.AllStatus,
          startDate: moment()
            .add(-initDayRange, 'days')
            .startOf('day')
            .toString(),
          endDate: moment()
            .endOf('day')
            .toString(),
        }),
      );
    },
    [initDayRange],
  );
  useEffect(
    () => {
      if (startDate && endDate) {
        dispatch(NewHistoryDuck.creators.fetchHistoryBookings());
      }
    },
    [startDate, endDate],
  );

  // order states
  const allOrderStates = useMemo(
    () =>
      Object.entries(NEW_COMPLETED_ORDER_STATE_FILTER).map(
        ([state, config]) => ({
          value: state,
          title: I18n.t(config.text),
          child: I18n.t(config.text),
        }),
      ),
    [],
  );
  const onChangeOrderState = useCallback(
    orderState => {
      if (!isPax) {
        dispatch(
          TrackGADuck.creators.trackGAExpressBookingsWebClick(
            'PAST_BOOKING_STATUS',
          ),
        );
      }

      dispatch(
        ScribeDuck.creators.scribeExpressBookingsWebHistoryBookingsStatus(),
      );
      dispatch(
        NewHistoryDuck.creators.updateHistoryBookingFilter({ orderState }),
      );
    },
    [dispatch],
  );

  // order type
  const allOrderTypes = useMemo(
    () =>
      Object.keys(ORDER_TYPES)
        .map(ot => ({
          value: ot,
          title: I18n.t(`history.orderTypes.${ot.toLowerCase()}`),
        }))
        .map(opt => ({
          value: opt.value,
          title: opt.title,
          child: opt.title,
          icon: opt.value === ORDER_TYPES.SHOPIFY ? shopifyLogo : undefined,
        })),
    [],
  );
  const onChangeOrderType = useCallback(
    orderType => {
      if (!isPax && orderType === ORDER_TYPES.SHOPIFY) {
        shopifyTrackCtx.fireShopifyTrackEvent({
          stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
          eventName: SCRIBE_SHOPIFY_EVENTS.FILTER_SHOPIFY_ORDERS,
        });
      }

      dispatch(
        NewHistoryDuck.creators.updateHistoryBookingFilter({
          orderType,
        }),
      );
    },
    [dispatch],
  );

  // date range picker
  const disabledDate = useCallback(
    dateToBeTested => {
      // older than max day range old
      if (dateToBeTested < moment().add(-OLDEST_HISTORY_FETCH_DAYS, 'days')) {
        return true;
      }
      // newer than now
      if (dateToBeTested > moment().endOf('day')) {
        return true;
      }

      if (tempStartDate) {
        const longerThanDayRange = dateToBeTested.isAfter(
          moment(tempStartDate).add(dayRange, 'days'),
        );
        const beforeStartDate = dateToBeTested.isBefore(moment(tempStartDate));
        return longerThanDayRange || beforeStartDate;
      }
      return false;
    },
    [dayRange, tempStartDate],
  );
  const onPickStartDate = useCallback(
    dates => {
      if (!dates || dates.length !== 1) return;
      setTempStartDate(dates[0]);
    },
    [dispatch, setTempStartDate],
  );
  const onDatePickerBlur = useCallback(
    () => {
      setTempStartDate(undefined);
    },
    [setTempStartDate],
  );
  const onPickDates = useCallback(
    dates => {
      if (dates && dates.length === 2) {
        if (!isPax) {
          dispatch(
            TrackGADuck.creators.trackGAExpressBookingsWebClick('DATE_RANGE'),
          );
        }

        dispatch(
          ScribeDuck.creators.scribeExpressBookingsWebHistoryBookingsDate(),
        );
        dispatch(
          NewHistoryDuck.creators.updateHistoryBookingFilter({
            startDate: dates[0].startOf('day').toString(),
            endDate: dates[1].endOf('day').toString(),
          }),
        );
      }
    },
    [dispatch],
  );

  return (
    <div className={commonTable.bookingFilter}>
      <Select
        placeholder=""
        options={allOrderTypes}
        className={commonTable.select}
        optionClassName={commonTable.selectOption}
        value={orderType}
        onChange={onChangeOrderType}
        disabled={loading}
      />
      <Select
        options={allOrderStates}
        className={commonTable.select}
        optionClassName={commonTable.selectOption}
        value={orderState}
        onChange={onChangeOrderState}
        disabled={loading}
      />
      <DatePicker.RangePicker
        className={commonTable.datePicker}
        disabledDate={disabledDate}
        onCalendarChange={onPickStartDate}
        onBlur={onDatePickerBlur}
        onChange={onPickDates}
        value={[moment(startDate), moment(endDate)]}
        disabled={loading}
      />
      {/* {!isPaxUser && ( */}
      {/*  <Button */}
      {/*    onClick={this.onClickExport(orders)} */}
      {/*    disabled={isPreviousBookingsLoading || _isEmpty(orders)} */}
      {/*  > */}
      {/*    {I18n.t('v2.bookingsPage.exportExcel')} */}
      {/*  </Button> */}
      {/* )} */}
    </div>
  );
};

export default HistoryBookingFilter;
